import {
  BOOK_PERMISSION,
  bookIdRegexStrict,
  CONTENT_CLASSES,
  TREE_LABEL_HIDE_IN_TREE
} from "./content.constants";
import { BOOK_REAL_TYPE } from "@/shared/utils/reader/constants";

/**
 * @param {string | number | {}} idOrBook
 * @return {string} e.g. "b127"
 */
export const getBookIdWithPrefix = (idOrBook) => {
  let id = idOrBook;
  if (id !== null && typeof id === 'object') {
    id = idOrBook.id || idOrBook.book_id;
  }
  if (!id) {
    return undefined;
  }
  if (bookIdRegexStrict.test(id)) {
    return id;
  }

  const value = Number.isInteger(id) ? id.toString() : id;
  const [book] = value.split(".");
  return "b" + book.replace(/[a-z]/, "");
};

export const getParagraph = (paraId, paragraphs) => {
  if (paraId) {
    const bookParas = paragraphs[getBookIdNumber(paraId)];
    if (bookParas) {
      const paragraph = bookParas.find((item) => item.id === paraId);
      if (paragraph) {
        return paragraph;
      }
    }
  }
  return undefined;
};

export const getBookIdNumber = (value) => {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    let outValue = value.split(".")[0].replace(/[a-z]/, "");
    return parseInt(outValue);
  }
  return value;
};

export const getChapterNumber = (value) => {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    let outValue = value.split(".")[1];
    return parseInt(outValue);
  }
  return value;
};

/**
 * method checkes ids for one book. canc check paraId and bookId
 * @param {*} paraId  123.45 or b123
 * @param {*} paraId1 123.45 or b123
 */
export const isSameBook = (paraId, paraId1) => {
  if (!paraId || !paraId1) {
    return false;
  }
  return getBookIdWithPrefix(paraId) === getBookIdWithPrefix(paraId1);
};

export const renderYearAndName = ({ pub_year: pubYear, pub_name: pubName }) => {
  const year = Number(pubYear) !== 0 ? pubYear : "";
  return (year ? `${year} ${pubName}` : pubName) || "";
};

export const removeBR = (text, newValue = " ") => {
  if (!text) {
    return "";
  }

  return text.replace(/<br\s*[/]?>/gi, newValue);
};

const makeChapter = (chapter) => ({
  ...chapter,
  label: chapter.title || chapter.refcode_short || chapter.para_id,
  className: CONTENT_CLASSES.CHAPTER,
  id: chapter.para_id,
});

export const parseTocInternal = (initialData, initialLevel) => {
  let tracklistIndex = 0;
  const toc = (data, level) => {
    if (!data.length) {
      return [];
    }
    const result = [];
    const accumulator = [];
    let lastChunk = null;
    for (let i = 0; i < data.length; i++) {
      const chap = makeChapter(data[i]);
      if (chap.level === level) {
        if (lastChunk != null) {
          const resultItem = lastChunk;
          if (lastChunk.mp3) {
            resultItem.cursor = tracklistIndex;
            tracklistIndex++;
          }
          resultItem.children = toc(accumulator, level + 1);
          result.push(resultItem);
          accumulator.length = 0;
        }
        lastChunk = chap;
      } else {
        accumulator.push(chap);
      }
    }
    const resultItem = lastChunk;
    if (lastChunk.mp3) {
      resultItem.cursor = tracklistIndex;
      tracklistIndex++;
    }
    resultItem.children = toc(accumulator, level + 1);
    result.push(resultItem);
    return result;
  };
  return toc(initialData, initialLevel);
};

export const convertChapters = (content) => {
  let cursor = 0;
  return content.map((item) => {
    const newItem = {
      ...item,
      className: CONTENT_CLASSES.CHAPTER,
      id: item.para_id,
    };
    if (item.mp3) {
      newItem.cursor = cursor;
      cursor++;
    }
    return newItem;
  });
};

export const makeChapterTree = (content) => {
  const children = parseTocInternal(content, 0);
  let bookChildren = children;
  if (children[0].children.length > 1) {
    bookChildren = children[0].children;
  }
  const chapters = convertChapters(content);
  return {
    children: bookChildren,
    chapters,
  };
};

export const cutOutHeaders = (dateContent) => {
  dateContent.forEach((item) => {
    item.content = item.content.filter((tag) => !tag.includes("</h1>"));
  });
  return dateContent;
};

export const normalizeSubsContent = (content) => {
  return content.map((item) => ({
    ...item,
    className: "subscription",
    // label: item?.book_title // text app crash build
    label: item ? item.book_title : undefined,
  }));
};

// [3360] e.g. replace "3,4,5" to "3-5" for "b2778"
export const normalizeBibleRefCode4 = (bookType, refcode4) => {
  if (bookType === "scriptindex" && typeof refcode4 === "string") {
    return refcode4.replace(
      /([\d]+),.*,\s([\d]+)/g,
      (fullMatch, from, to) => {
        return from + " - " + to;
      },
    );
  }

  return "";
};

export const isBookRealTypeBible = (realType = "") => realType.indexOf("bible") === 0;

export const isBookRealTypeBibleCommentaries = (realType) => realType === BOOK_REAL_TYPE.bibleCommentaries;

// Special requirement from D - show paragraphs level in Tree, BC, TOC for any bible.
export const isShowParaLevelByBookRealType = (realType) => {
  return realType ? isBookRealTypeBible(realType) : false;
};

export const makeParaDataFinal = (bookType, bookRealType, para, indexPara) => {
  const isRealTypeBible = isBookRealTypeBible(bookRealType);

  let labelTree;

  /* === Bible has para level, thus "Tree" and "BC" labels should be parsed differently. === */
  let label = para.refcode_4 || para.refcode_short || [
    para.refcode_1, para.refcode_2, para.refcode_3, para.refcode_4,
  ].filter(Boolean).join(" ") || indexPara;

  if (isRealTypeBible) {
    const refCode4Normalized = normalizeBibleRefCode4(bookType, para.refcode_4);

    // "BC" label
    if (para.refcode_2 && para.refcode_3 && refCode4Normalized) {
      if (refCode4Normalized === "0") {
        label = para.refcode_2 + " " + para.refcode_3;
      } else {
        label = para.refcode_2 + " " + para.refcode_3 + ":" + refCode4Normalized;
      }
    } else {
      label = para.refcode_long || para.refcode_1;
    }
    // ======================================

    // [3360][3365] e.g. remove unnecessary "h4" for "b2778"
    if (bookType === "scriptindex" && para.element_type === "h4") {
      labelTree = TREE_LABEL_HIDE_IN_TREE;
    } else {
      // "Tree" label
      if (para.refcode_3 && refCode4Normalized) {
        if (refCode4Normalized === "0") {
          labelTree = para.refcode_3;
        } else {
          labelTree = para.refcode_3 + ":" + refCode4Normalized;
        }
      } else {
        labelTree = para.refcode_short || para.refcode_1;
      }
      // ======================================
    }
  }
  /* ======================================================================================= */

  return {
    labelTreeFinal: labelTree,
    labelFinal: label,
  };
};

export const removeTags = (text = "", newValue = "") => {
  const re = /(<([^>]+)>)/g;

  if (re.test(text)) {
    return text.replace(/(<([^>]+)>)/g, newValue);
  }

  return text;
};

/**
 * Method for search object by id in json tree.
 *
 * @param {*} value  - id or other value for search
 * @param {*} currentNode - json tree
 * @param {string} field - property name to search
 * @param {Array} nodeList - holder of all parent for found value
 * @returns (Object|Boolean);
 */
export const findNode = (
  value, currentNode, field = "id", nodeList, callValueAsFunction
) => {
  let i, currentChild, result, children;

  if (callValueAsFunction ? value(currentNode) : value == currentNode[field]) {
    return currentNode;
  }
  if (currentNode.length) {
    children = currentNode;
  } else {
    children = currentNode.children;
  }
  if (children) {
    for (i = 0; i < children.length; i += 1) {
      currentChild = children[i];
      result = findNode(value, currentChild, field, nodeList, callValueAsFunction);
      if (result !== false) {
        if (nodeList) {
          nodeList.push(currentChild);
        }
        return result;
      }
    }
  }
  return false;
};

export const isTreeItemExpandable = (item, isShowParaLevel) => {
  const itemClassNameOrType = item.className || item.type;

  if (itemClassNameOrType === CONTENT_CLASSES.CHAPTER) {
    if (isShowParaLevel) {
      return true;
    }

    const itemChildren = item.children || [];
    // If the first child is a chapter, then the others too.
    // If the children are chapters, then the parent should be expandable.
    return itemChildren[0] && itemChildren[0].className === CONTENT_CLASSES.CHAPTER;
  }

  return itemClassNameOrType !== CONTENT_CLASSES.CATEGORIES_BOOK_TRANSLATION
    && itemClassNameOrType !== CONTENT_CLASSES.TITLES_BOOK_TRANSLATION
    && itemClassNameOrType !== CONTENT_CLASSES.PARAGRAPH;
};

export const isBookForSale = (book) => {
  if (!book) {
    return undefined;
  }

  return book.permission_required === BOOK_PERMISSION.authenticated;
};

export const isBookPermissionPurchaseOK = (shopBook) => {
  if (shopBook) {
    return shopBook.is_purchased;
  }

  return true;
};

export const isBookPermissionAuthOK = (book, isLogin) => {
  if (!book) {
    return undefined;
  }

  if (isBookForSale(book)) {
    return isLogin;
  }

  return true;
};

export const isBookPermissionsOK = (shopBook, book, isLogin) => {
  return isBookPermissionPurchaseOK(shopBook) && isBookPermissionAuthOK(book, isLogin);
};

const FirstCharRegEx = /^(the\s|a\s|an\s|from\s|"|\()(.*)/;

// TODO make fix for get only book char for first char
// 'The $150,000 Fund' - first char should be "o", bcz of "1(one)".
export const getFirstTitleChar = (value) => {
  let title = value.toLowerCase();
  let match = title.match(FirstCharRegEx);
  if (match && match[2]) {
    title = match[2];
  }
  return title.trim().substr(0, 1);
};
